import {BarChart2, Calendar} from "react-feather";
import {Button, Container, Spinner, Tab, Tabs} from "react-bootstrap";
import useTranslation from "../hooks/translations";
import DefaultLayout from "../layouts/DefaultLayout";
import HorizontalBarChart from "../components/HorizontalBarChart";
import {useApi} from "../contexts/ApiProvider";
import {forwardRef, useCallback, useEffect, useMemo, useState} from "react";
import CostService from "../services/CostService";
import IncomeService from "../services/IncomeService";
import CurrencyUtils from "../utils/CurrencyUtils";
import DatesUtils from "../utils/dates";
import {useDateRange} from "../contexts/DateRangeProvider";
import IncomeTypeService from "../services/IncomeTypeService";
import CostTypeService from "../services/CostTypeService";
import RadarChart from "../components/RadarChart";
import useMonths from "../hooks/months";
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import CostCategoryService from "../services/CostCategoryService";
import IncomeCategoryService from "../services/IncomeCategoryService";
import ProtectedContent from "../components/ProtectedContent";

export default function ComparativeReportPage() {
  const months = useMonths();
  const translations = useTranslation();

  const pageTitle = translations.comparativeReport;
  const pageSubtitle = translations.comparativeReportSubtitle;

  const {from} = useDateRange();

  const [year, setYear] = useState(from.getFullYear());
  const [month, setMonth] = useState(from.getMonth());

  const [firstMonth, setFirstMonth] = useState(new Date(year, month, 1));
  const [secondMonth, setSecondMonth] = useState(new Date(year, month - 1, 1));

  const api = useApi();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFirstMonth, setIsLoadingFirstMonth] = useState(false);
  const [isLoadingSecondMonth, setIsLoadingSecondMonth] = useState(false);
  const costService = useMemo(() => new CostService(api), [api]);
  const incomeService = useMemo(() => new IncomeService(api), [api]);
  const costTypeService = useMemo(() => new CostTypeService(api), [api]);
  const incomeTypeService = useMemo(() => new IncomeTypeService(api), [api]);
  const costCategoryService = useMemo(() => new CostCategoryService(api), [api]);
  const incomeCategoryService = useMemo(() => new IncomeCategoryService(api), [api]);

  const [costTypes, setCostTypes] = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);

  const types = useMemo(() => [...costTypes, ...incomeTypes], [costTypes, incomeTypes]);

  const getCostTypes = useCallback(async () => {
    const response = await costTypeService.getAll();
    setCostTypes(response.body.map((costType) => costType.name));
  }, [costTypeService]);

  const getIncomeTypes = useCallback(async () => {
    const response = await incomeTypeService.getAll();
    setIncomeTypes(response.body.map((incomeType) => incomeType.name));
  }, [incomeTypeService]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await Promise.all([
        getCostTypes(),
        getIncomeTypes()
      ]);
      setIsLoading(false);
    })();
  }, [getCostTypes, getIncomeTypes]);

  const [costCategories, setCostCategories] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState([]);

  const categories = useMemo(() => [...costCategories, ...incomeCategories], [costCategories, incomeCategories]);

  const getCostCategories = useCallback(async () => {
    const response = await costCategoryService.getAll();
    setCostCategories(response.body.map((costCategory) => costCategory.name));
  }, [costCategoryService]);

  const getIncomeCategories = useCallback(async () => {
    const response = await incomeCategoryService.getAll();
    setIncomeCategories(response.body.map((incomeCategory) => incomeCategory.name));
  }, [incomeCategoryService]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await Promise.all([
        getCostCategories(),
        getIncomeCategories()
      ]);
      setIsLoading(false);
    })();
  }, [getCostCategories, getIncomeCategories]);

  const [costsDataFirstMonth, setCostsDataFirstMonth] = useState([]);
  const [incomesDataFirstMonth, setIncomesDataFirstMonth] = useState([]);

  const [costsDataSecondMonth, setCostsDataSecondMonth] = useState([]);
  const [incomesDataSecondMonth, setIncomesDataSecondMonth] = useState([]);

  const getCostsData = useCallback(async (start, end) => {
    const response = await costService.search(start, end);
    return response.body.map((row) => {
      const dateObject = new Date(row.timestamp);
      const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
        row.currency.symbol
      );
      return {
        id: row.id,
        name: row.name,
        type: row.type.name,
        typeId: row.type.id,
        category: row.category.name,
        categoryId: row.category.id,
        amountFormatted: currencyFormatter.format(row.amount),
        amount: row.amount,
        date: DatesUtils.formatDate(dateObject),
        dateFormatted: DatesUtils.format(dateObject),
        currencyId: row.currency.id
      }
    });
  }, [costService]);

  const getIncomesData = useCallback(async (start, end) => {
    const response = await incomeService.search(start, end);
    return response.body.map((row) => {
      const dateObject = new Date(row.timestamp);
      const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
        row.currency.symbol
      );
      return {
        id: row.id,
        name: row.name,
        type: row.type.name,
        typeId: row.type.id,
        category: row.category.name,
        categoryId: row.category.id,
        amountFormatted: currencyFormatter.format(row.amount),
        amount: row.amount,
        date: DatesUtils.formatDate(dateObject),
        dateFormatted: DatesUtils.format(dateObject),
        currencyId: row.currency.id
      }
    });
  }, [incomeService]);

  useEffect(() => {
    (async () => {
      setIsLoadingFirstMonth(true);
      const from = firstMonth;
      const to = new Date(firstMonth.getFullYear(), firstMonth.getMonth() + 1, 0);
      await Promise.all([
        getCostsData(from, to),
        getIncomesData(from, to)
      ]).then(([costs, incomes]) => {
        setCostsDataFirstMonth(costs);
        setIncomesDataFirstMonth(incomes);
        setIsLoadingFirstMonth(false);
      });
    })();
  }, [firstMonth, getCostsData, getIncomesData]);

  useEffect(() => {
    (async () => {
      setIsLoadingSecondMonth(true);
      const from = secondMonth;
      const to = new Date(secondMonth.getFullYear(), secondMonth.getMonth() + 1, 0);
      await Promise.all([
        getCostsData(from, to),
        getIncomesData(from, to)
      ]).then(([costs, incomes]) => {
        setCostsDataSecondMonth(costs);
        setIncomesDataSecondMonth(incomes);
        setIsLoadingSecondMonth(false);
      });
    })();
  }, [secondMonth, getCostsData, getIncomesData]);

  const [costsChartDatasetByTypeFirstMonth, setCostsChartDatasetByTypeFirstMonth] = useState({
    data: []
  });

  const [incomesChartDatasetByTypeFirstMonth, setIncomesChartDatasetByTypeFirstMonth] = useState({
    data: []
  });

  const [costsChartDatasetByTypeSecondMonth, setCostsChartDatasetByTypeSecondMonth] = useState({
    data: []
  });

  const [incomesChartDatasetByTypeSecondMonth, setIncomesChartDatasetByTypeSecondMonth] = useState({
    data: []
  });

  const [costsChartDatasetByCategoryFirstMonth, setCostsChartDatasetByCategoryFirstMonth] = useState({
    data: []
  });

  const [incomesChartDatasetByCategoryFirstMonth, setIncomesChartDatasetByCategoryFirstMonth] = useState({
    data: []
  });

  const [costsChartDatasetByCategorySecondMonth, setCostsChartDatasetByCategorySecondMonth] = useState({
    data: []
  });

  const [incomesChartDatasetByCategorySecondMonth, setIncomesChartDatasetByCategorySecondMonth] = useState({
    data: []
  });

  const getEntriesGroupedByType = (entries) => {
    return entries.reduce((groupedEntries, entry) => {
      const {type} = entry;
      groupedEntries[type] = groupedEntries[type] ?? 0;
      groupedEntries[type] += entry.amount;
      return groupedEntries;
    }, {});
  }

  const getEntriesGroupedByCategory = (entries) => {
    return entries.reduce((groupedEntries, entry) => {
      const {category} = entry;
      groupedEntries[category] = groupedEntries[category] ?? 0;
      groupedEntries[category] += entry.amount;
      return groupedEntries;
    }, {});
  }

  const getChartData = (types, values, defaultValue = 0) => {
    return types.map(type => values[type] ?? defaultValue);
  };

  useEffect(() => {
    const costsGroupedByType = getEntriesGroupedByType(costsDataFirstMonth);
    setCostsChartDatasetByTypeFirstMonth({
      label: translations.costs,
      data: getChartData(types, costsGroupedByType),
      borderColor: "rgba(232, 21, 0, 1)",
      backgroundColor: "rgba(232, 21, 0, 0.1)",
      borderWidth: 2
    });

    const costsGroupedByCategory = getEntriesGroupedByCategory(costsDataFirstMonth);
    setCostsChartDatasetByCategoryFirstMonth({
      label: translations.costs,
      data: getChartData(categories, costsGroupedByCategory),
      borderColor: "rgba(232, 21, 0, 1)",
      backgroundColor: "rgba(232, 21, 0, 0.1)",
      borderWidth: 2
    });
  }, [types, categories, costsDataFirstMonth]);

  useEffect(() => {
    const incomesGroupedByType = getEntriesGroupedByType(incomesDataFirstMonth);
    setIncomesChartDatasetByTypeFirstMonth({
      label: translations.incomes,
      data: getChartData(types, incomesGroupedByType),
      borderColor: "rgba(0, 172, 105, 1)",
      backgroundColor: "rgba(0, 172, 105, 0.1)",
      borderWidth: 2
    });

    const incomesGroupedByCategory = getEntriesGroupedByCategory(incomesDataFirstMonth);
    setIncomesChartDatasetByCategoryFirstMonth({
      label: translations.incomes,
      data: getChartData(categories, incomesGroupedByCategory),
      borderColor: "rgba(0, 172, 105, 1)",
      backgroundColor: "rgba(0, 172, 105, 0.1)",
      borderWidth: 2
    });
  }, [types, categories, incomesDataFirstMonth]);

  useEffect(() => {
    const costsGroupedByType = getEntriesGroupedByType(costsDataSecondMonth);
    setCostsChartDatasetByTypeSecondMonth({
      label: translations.costs,
      data: getChartData(types, costsGroupedByType),
      borderColor: "rgba(232, 21, 0, 1)",
      backgroundColor: "rgba(232, 21, 0, 0.1)",
      borderWidth: 2
    });

    const costsGroupedByCategory = getEntriesGroupedByCategory(costsDataSecondMonth);
    setCostsChartDatasetByCategorySecondMonth({
      label: translations.costs,
      data: getChartData(categories, costsGroupedByCategory),
      borderColor: "rgba(232, 21, 0, 1)",
      backgroundColor: "rgba(232, 21, 0, 0.1)",
      borderWidth: 2
    });
  }, [types, categories, costsDataSecondMonth]);

  useEffect(() => {
    const incomesGroupedByType = getEntriesGroupedByType(incomesDataSecondMonth);
    setIncomesChartDatasetByTypeSecondMonth({
      label: translations.incomes,
      data: getChartData(types, incomesGroupedByType),
      borderColor: "rgba(0, 172, 105, 1)",
      backgroundColor: "rgba(0, 172, 105, 0.1)",
      borderWidth: 2
    });

    const incomesGroupedByCategory = getEntriesGroupedByCategory(incomesDataSecondMonth);
    setIncomesChartDatasetByCategorySecondMonth({
      label: translations.incomes,
      data: getChartData(categories, incomesGroupedByCategory),
      borderColor: "rgba(0, 172, 105, 1)",
      backgroundColor: "rgba(0, 172, 105, 0.1)",
      borderWidth: 2
    });
  }, [types, categories, incomesDataSecondMonth]);

  const MonthPickerCustomInput = forwardRef(
    ({value, onClick, className}, ref) => (
      <Button variant="link outline-primary" className={className}
              onClick={onClick} ref={ref}>
        <Calendar/>
      </Button>
    ),
  );

  return (<DefaultLayout icon={<BarChart2/>}
                         title={pageTitle}
                         subtitle={pageSubtitle}>
    <ProtectedContent
      allowedRoles={["Admin", "Moderator"]}
      alt={<div style={{textAlign: "center", margin: 10}}>
        <h1>{translations.permissionsAlert}</h1></div>}>
      <Container fluid="xl" className="px-4 mt-n10">
        <div className="row">
          <div className="col-xl-6 mb-4">
            <div className="card card-header-actions mx-auto">
              <div className="card-header border-bottom">
                {months[firstMonth.getMonth()]}, {firstMonth.getFullYear()}
                <div>
                  <DatePicker
                    selected={firstMonth}
                    onChange={(date) => setFirstMonth(date)}
                    dateFormat="MMMM, yyyy"
                    showMonthYearPicker
                    customInput={<MonthPickerCustomInput/>}
                    popperPlacement="bottom-end"
                    popperClassName="month-datepicker-popper"
                  />
                </div>
              </div>
              <div className="card-body">
                <Tabs
                  id="first-month-tabs"
                  defaultActiveKey="first-month-by-types"
                  className="mb-3"
                  justify
                >
                  <Tab eventKey="first-month-by-types"
                       title={translations.byType}>
                    {isLoadingFirstMonth ? <Spinner/> :
                      <>
                        <HorizontalBarChart chartData={{
                          labels: types,
                          datasets: [
                            costsChartDatasetByTypeFirstMonth,
                            incomesChartDatasetByTypeFirstMonth
                          ]
                        }} aspectRatio={1.5}/>
                        <RadarChart chartData={{
                          labels: types,
                          datasets: [
                            costsChartDatasetByTypeFirstMonth,
                            incomesChartDatasetByTypeFirstMonth
                          ]
                        }}/>
                      </>}
                  </Tab>
                  <Tab eventKey="first-month-by-categories"
                       title={translations.byCategory}>
                    {isLoadingFirstMonth ? <Spinner/> :
                      <>
                        <HorizontalBarChart chartData={{
                          labels: categories,
                          datasets: [
                            costsChartDatasetByCategoryFirstMonth,
                            incomesChartDatasetByCategoryFirstMonth
                          ]
                        }} aspectRatio={0.5}/>
                        <RadarChart chartData={{
                          labels: categories,
                          datasets: [
                            costsChartDatasetByCategoryFirstMonth,
                            incomesChartDatasetByCategoryFirstMonth
                          ]
                        }}/>
                      </>}
                  </Tab>
                </Tabs>
              </div>
            </div>
          </div>
          <div className="col-xl-6 mb-4">
            <div className="card card-header-actions mx-auto">
              <div className="card-header border-bottom">
                {months[secondMonth.getMonth()]}, {secondMonth.getFullYear()}
                <div>
                  <DatePicker
                    selected={secondMonth}
                    onChange={(date) => setSecondMonth(date)}
                    dateFormat="MMMM, yyyy"
                    showMonthYearPicker
                    customInput={<MonthPickerCustomInput/>}
                    popperPlacement="bottom-end"
                    popperClassName="month-datepicker-popper"
                  />
                </div>
              </div>
              <div className="card-body">
                <Tabs
                  id="second-month-tabs"
                  defaultActiveKey="second-month-by-types"
                  className="mb-3"
                  justify
                >
                  <Tab eventKey="second-month-by-types"
                       title={translations.byType}>
                    {isLoadingSecondMonth ? <Spinner/> :
                      <>
                        <HorizontalBarChart chartData={{
                          labels: types,
                          datasets: [
                            costsChartDatasetByTypeSecondMonth,
                            incomesChartDatasetByTypeSecondMonth
                          ]
                        }} aspectRatio={1.5}/>
                        <RadarChart chartData={{
                          labels: types,
                          datasets: [
                            costsChartDatasetByTypeSecondMonth,
                            incomesChartDatasetByTypeSecondMonth
                          ]
                        }}/>
                      </>}
                  </Tab>
                  <Tab eventKey="second-month-by-categories"
                       title={translations.byCategory}>
                    {isLoadingSecondMonth ? <Spinner/> :
                      <>
                        <HorizontalBarChart chartData={{
                          labels: categories,
                          datasets: [
                            costsChartDatasetByCategorySecondMonth,
                            incomesChartDatasetByCategorySecondMonth
                          ]
                        }} aspectRatio={0.5}/>
                        <RadarChart chartData={{
                          labels: categories,
                          datasets: [
                            costsChartDatasetByCategorySecondMonth,
                            incomesChartDatasetByCategorySecondMonth
                          ]
                        }}/>
                      </>}
                  </Tab>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </ProtectedContent>
  </DefaultLayout>);
}
